import type { ReactNode } from 'react';
import React from 'react';
import dynamic from 'next/dynamic';
import { styled } from '@components-shared/src/stitches';
import { useAuth } from '@context/auth/base';
import { HeaderV2 } from './HeaderV2';
import { LeftNavType, useLeftNavType } from './helpers';

export const LEFT_NAV_WIDTH_COLLAPSED = 52;
export const LEFT_NAV_WIDTH_FULL = 240;

const DashboardSections = dynamic(() =>
  import('./DashboardSections').then(mod => mod.DashboardSections),
);
const EditUploadSection = dynamic(() =>
  import('./EditUploadSection').then(mod => mod.EditUploadSection),
);
const UploadSection = dynamic(() => import('./UploadSection').then(mod => mod.UploadSection));

export const LeftNav = () => {
  const { authUser } = useAuth();
  const leftNavType = useLeftNavType();

  if (leftNavType === LeftNavType.HIDDEN) return null;

  let content: ReactNode;
  if (leftNavType === LeftNavType.DEFAULT) {
    content = null;
  } else if (leftNavType === LeftNavType.DASHBOARD && authUser) {
    content = <DashboardSections authUser={authUser} />;
  } else if (leftNavType === LeftNavType.UPLOAD && authUser) {
    content = <UploadSection />;
  } else if (leftNavType === LeftNavType.EDIT_UPLOAD && authUser) {
    content = <EditUploadSection />;
  }

  return (
    <Container>
      <HeaderV2 leftNavType={leftNavType} />
      {content}
    </Container>
  );
};

const Container = styled('div', {
  backgroundColor: '$white',
  lift: 'secondary',
  zIndex: '$leftNav',
  position: 'fixed',
  left: 0,
  top: 0,
  width: LEFT_NAV_WIDTH_COLLAPSED,
  height: '100vh',
  display: 'none',
  transition: 'all 0.1s ease-in-out',

  '.indicator': {
    right: 5,
    top: 6,
    border: '0.75px solid $white',
  },
  '&:hover, &:active': {
    width: LEFT_NAV_WIDTH_FULL,
    h3: {
      visibility: 'visible',
      overflow: 'visible',
      width: 'unset',
    },
    '.logo-container': {
      alignSelf: 'flex-start',
    },
    '.logo-with-label': {
      display: 'inline',
      marginLeft: 20,
    },
    '.logo': {
      display: 'none',
    },
    '.expanded-header': {
      display: 'flex',
      a: {
        paddingLeft: 16,
        justifyContent: 'unset',
      },
    },
    '.collapsed-header': {
      display: 'none',
    },
    '.upcoming-header': {
      visibility: 'visible',
      width: '100%',
      overflow: 'visible',
    },
    '.expanded-upcoming-row': {
      visibility: 'visible',
      width: '100%',
      overflow: 'visible',
      marginRight: 8,
    },
    '.upcoming-row': {
      img: {
        marginRight: 8,
        marginLeft: 8,
      },
    },
    '.upcoming-section-container': {
      paddingX: 12,
    },
    '.library-link, .library-button': {
      justifyContent: 'flex-start',
      paddingLeft: 8,
    },
    '.indicator': {
      right: 8,
      top: 'unset',
      border: 'none',
    },
    '.playlist-section': {
      visibility: 'visible',
    },
    '.playlist-divider': {
      visibility: 'visible',
    },
    '.library-label': {
      display: 'inline',
    },
    '.label-with-arrow': {
      justifyContent: 'unset',
      paddingLeft: 8,
      span: {
        display: 'inline',
      },
    },
    '.progress-bar': {
      display: 'flex',
    },
    '.stepName': {
      display: 'block',
    },
    '.upload-section': {
      justifyContent: 'flex-start',
      paddingLeft: 20,
    },
    '.edit-upload-container': {
      display: 'flex',
    },
    '.help-icon-button': {
      display: 'none',
    },
    '.help-button': {
      display: 'flex',
    },
  },
  '@tablet': {
    display: 'flex',
    flexDirection: 'column',
  },
  '@laptop': {
    width: LEFT_NAV_WIDTH_FULL,
    '.indicator': {
      right: 8,
      top: 'unset',
      border: 'none',
    },
  },
});
